import { useEffect } from 'react'

import { logger } from '@/lib/logger'
import { pageImportFunctions } from '@/routes/pages'

/**
 * Hook that prefetches all page chunks during browser idle time.
 *
 * This runs after the initial page renders, downloading all lazy-loaded
 * pages in the background to make navigation instant. It includes:
 * - Uses requestIdleCallback to prefetch only when browser is idle
 * - Falls back to setTimeout if requestIdleCallback unavailable
 * - Respects data saver mode if enabled
 * - Silent error handling for failed prefetches
 *
 * Suitable for small apps (< 20 pages) where users are likely to
 * navigate between many pages in a session.
 */
export function usePrefetchPages() {
  useEffect(() => {
    // Respect data saver mode if enabled
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    if (connection?.saveData) {
      logger.info('Data saver mode detected, skipping page prefetch')
      return
    }

    const scheduleWork = window.requestIdleCallback || ((cb: IdleRequestCallback) => setTimeout(cb, 1))
    const cancelWork = window.cancelIdleCallback || clearTimeout

    // Schedule prefetching during idle time
    const handleId = scheduleWork(() => {
      // Prefetch all pages in parallel during idle time
      pageImportFunctions.forEach(importFn => {
        importFn().catch(err => {
          logger.warn('Failed to prefetch page:', err)
        })
      })
    })

    return () => cancelWork(handleId)
  }, [])
}
